import { useState, useEffect } from 'react';
import { Tag } from 'antd';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import {
  BadgeCheck as BadgeCheckIcon,
  Calendar as CalendarIcon,
  ChevronRight as ChevronRightIcon,
  Clock as ClockIcon,
  FileLock2 as FileLock2Icon,
  Mail as MailIcon,
  Plus as PlusIcon,
  Settings as SettingsIcon,
  ShieldCheck as ShieldCheckIcon,
  User as UserIcon,
  Users as UsersIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useAppSelector } from '@/store/hooks';
import { useOrganization } from '@/hooks/useOrganization';
import { usePermissions } from '@/hooks/usePermissions';
import { roleLabels, roleColors } from '@/types/organization';
import { SetupWalletBanner } from '@/components/common/SetupWalletBanner';
import { Identicon } from '@/components/common/Identicon';
import { truncateAddress } from '@/lib/addressDisplay';
import { mapWithConcurrency } from '@/lib/concurrency';
import { GettingStartedChecklist } from '@/components/dashboard/GettingStartedChecklist';
import type { GettingStartedStep } from '@/components/dashboard/GettingStartedChecklist';
import { TreasuryHero } from '@/components/dashboard/TreasuryHero';
import { vaultsApi } from '@/api/vaults';
import type { Vault } from '@/types/vault';
import { DemoWalkthroughCta } from '@/demo/DemoWalkthroughCta';

export function DashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.auth.user);
  const members = useAppSelector((state) => state.organization.members);
  const myInvitations = useAppSelector((state) => state.organization.myInvitations);
  const orgInvitations = useAppSelector((state) => state.organization.orgInvitations);
  const { currentOrgId, organizations, currentRole } = useOrganization();
  const isLoading = useAppSelector((state) => state.organization.isLoading);
  const signingRequests = useAppSelector((state) => state.vault.signingRequests);
  const { canInviteMembers, canEditOrg } = usePermissions();

  const [orgVaults, setOrgVaults] = useState<Vault[]>([]);
  // Whether any proposal in any vault has at least one signature — drives the
  // final "First proposal signed" activation step. Only probed once an active
  // vault exists (so the request fan-out is bounded and meaningful).
  const [hasSignedProposal, setHasSignedProposal] = useState(false);
  // The checklist's steps 3-5 derive from these two async fetches, which start
  // empty. Rendering the checklist before they settle flashes an intermediate
  // count that then vanishes once a fully-onboarded org resolves to allDone.
  // Gate the checklist render on both fetches having settled.
  const [vaultsLoaded, setVaultsLoaded] = useState(false);
  const [signedProbeSettled, setSignedProbeSettled] = useState(false);

  useEffect(() => {
    if (!currentOrgId) return;
    let cancelled = false;
    // Defer the re-gate out of the effect body (synchronous setState in an
    // effect triggers cascading renders); the fetch's finally flips it true.
    void Promise.resolve().then(() => {
      if (!cancelled) setVaultsLoaded(false);
    });
    vaultsApi
      .getVaults(currentOrgId)
      .then((vaults) => {
        if (!cancelled) setOrgVaults(vaults);
      })
      .catch(() => {
        if (!cancelled) setOrgVaults([]);
      })
      .finally(() => {
        if (!cancelled) setVaultsLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [currentOrgId]);

  const currentOrg = organizations.find((o) => o.id === currentOrgId);
  const pendingOrgInvitations = orgInvitations.filter((i) => i.status === 'pending').length;
  const showSetupBanner = !user?.wkIdentity || user.accountStatus === 'pending_wk';

  const activeVaults = orgVaults.filter((v) => v.status === 'active').length;
  const pendingSetupVaults = orgVaults.filter((v) => v.status === 'pending_setup').length;

  // Detect a first signed proposal across active vaults. Bounded: runs only
  // when an active vault exists, fetches a tiny page per vault, and bails out
  // as soon as it finds one signature. Keeps the activation checklist honest
  // without a dedicated endpoint.
  const activeVaultIds = orgVaults
    .filter((v) => v.status === 'active')
    .map((v) => v.id)
    .join(',');
  useEffect(() => {
    let cancelled = false;
    if (!currentOrgId || !activeVaultIds) {
      // No active vault to probe: nothing to wait for, mark settled.
      // Resolve asynchronously so we never call setState during the effect body.
      void Promise.resolve().then(() => {
        if (!cancelled) {
          setHasSignedProposal(false);
          setSignedProbeSettled(true);
        }
      });
      return () => {
        cancelled = true;
      };
    }
    // Re-gate while the probe is in flight (deferred to avoid an in-effect
    // setState); resolves true below so the checklist doesn't render step 5
    // with stale data on a re-run after vaults load.
    void Promise.resolve().then(() => {
      if (!cancelled) setSignedProbeSettled(false);
    });
    const ids = activeVaultIds.split(',');
    // Capped fan-out: one request per vault, but never all vaults at once so a
    // large org doesn't burst into the relay's per-IP rate limit on page load.
    void mapWithConcurrency(ids, 4, (vid) =>
      vaultsApi
        .getProposals(currentOrgId, vid, { limit: 20 })
        .then((res) => res.proposals.some((p) => p.signatureCount > 0))
        .catch(() => false)
    ).then((results) => {
      if (!cancelled) {
        setHasSignedProposal(results.some(Boolean));
        setSignedProbeSettled(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [currentOrgId, activeVaultIds]);

  // Real activation state for the Getting Started checklist. Each flag is
  // derived from live Redux/API data and deep-links the incomplete step.
  const hasTeam = members.length > 1 || pendingOrgInvitations > 0;
  const gettingStartedSteps: GettingStartedStep[] = [
    {
      key: 'org',
      label: 'Create your organization',
      description: 'Set up an organization to coordinate your team.',
      done: organizations.length > 0,
      to: '/organizations?create=true',
      cta: 'Create',
    },
    {
      key: 'team',
      label: 'Invite your team',
      description: 'Add co-signers and members to your organization.',
      done: hasTeam,
      to: '/invitations',
      cta: 'Invite',
    },
    {
      key: 'vault',
      label: 'Create a vault',
      description: 'Set up a multisig vault to hold treasury funds.',
      done: orgVaults.length > 0,
      to: '/vaults/create',
      cta: 'Create',
    },
    {
      key: 'activate',
      label: 'Activate your vault',
      description: 'Every signer provides their keys to activate the vault.',
      done: activeVaults > 0,
      to: '/vault-setup',
      cta: 'Provide keys',
    },
    {
      key: 'sign',
      label: 'Sign your first proposal',
      description: 'Propose and co-sign a transaction from a vault.',
      done: hasSignedProposal,
      to: '/signing-requests',
      cta: 'Review',
    },
  ];

  // Show loader while organizations are being fetched
  if (isLoading) {
    return <LoadingSkeleton variant="detail" />;
  }

  // If no organization selected
  if (!currentOrgId) {
    return (
      <div>
        <div className="welcome-banner">
          <div className="welcome-banner-content">
            <h1 className="welcome-banner-title">{t('dashboard.welcomeTitle')}</h1>
            <p className="welcome-banner-subtitle">
              {organizations.length > 0
                ? t('dashboard.welcomeSubtitleHasOrgs')
                : t('dashboard.welcomeSubtitleNoOrgs')}
            </p>
            <div className="quick-actions">
              {organizations.length > 0 ? (
                <button
                  className="quick-action-btn primary"
                  onClick={() => navigate('/organizations')}
                >
                  <UsersIcon />
                  {t('dashboard.viewOrganizations')}
                </button>
              ) : (
                <button
                  className="quick-action-btn primary"
                  onClick={() => navigate('/organizations?create=true')}
                >
                  <PlusIcon />
                  {t('dashboard.createOrganization')}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Setup Banner for pending_wk users */}
        {showSetupBanner && <SetupWalletBanner />}

        {myInvitations.length > 0 && (
          <div className="dashboard-section">
            <div className="dashboard-section-header">
              <h2 className="dashboard-section-title">Pending Invitations</h2>
              <button className="dashboard-section-link" onClick={() => navigate('/notifications')}>
                View all <ChevronRightIcon />
              </button>
            </div>
            <div className="info-card invitation-banner">
              <div className="invitation-banner-content">
                <div className="invitation-banner-icon">
                  <MailIcon />
                </div>
                <div className="invitation-banner-text">
                  <p className="info-card-text">
                    You have <strong className="text-warning">{myInvitations.length}</strong>{' '}
                    pending invitation{myInvitations.length !== 1 ? 's' : ''} waiting for your
                    response.
                  </p>
                </div>
                <button
                  className="quick-action-btn primary"
                  onClick={() => navigate('/notifications')}
                >
                  Review
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="dashboard-section">
          <div className="dashboard-section-header">
            <h2 className="dashboard-section-title">Your Account</h2>
          </div>
          <div className="info-card">
            <div className="info-card-row">
              <span className="info-card-label">SSP Identity</span>
              <span className="info-card-value">
                <code>{user?.wkIdentity}</code>
              </span>
            </div>
            <div className="info-card-row">
              <span className="info-card-label">Enterprise Email</span>
              <span className="info-card-value">
                {user?.enterpriseEmail ? (
                  user.enterpriseEmail
                ) : (
                  <button className="dashboard-section-link" onClick={() => navigate('/profile')}>
                    Set up email <ChevronRightIcon />
                  </button>
                )}
              </span>
            </div>
            <div className="info-card-row">
              <span className="info-card-label">Organizations</span>
              <span className="info-card-value">{organizations.length}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Welcome Banner */}
      <div className="welcome-banner">
        <div className="welcome-banner-content">
          <div className="dashboard-header-row">
            <h1 className="welcome-banner-title">{currentOrg?.name}</h1>
            {currentRole && <Tag color={roleColors[currentRole]}>{roleLabels[currentRole]}</Tag>}
          </div>
          {currentOrg?.description && (
            <p className="welcome-banner-subtitle">{currentOrg.description}</p>
          )}
          <div className="quick-actions">
            {canInviteMembers && (
              <button className="quick-action-btn primary" onClick={() => navigate('/invitations')}>
                <PlusIcon />
                {t('dashboard.inviteMembers')}
              </button>
            )}
            <button className="quick-action-btn" onClick={() => navigate('/members')}>
              <UserIcon />
              {t('dashboard.viewTeam')}
            </button>
            {canEditOrg && (
              <button className="quick-action-btn" onClick={() => navigate('/settings')}>
                <SettingsIcon />
                {t('dashboard.settings')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Setup Banner for pending_wk users */}
      {showSetupBanner && <SetupWalletBanner />}

      {/* Demo-only signature walkthrough entry (self-gates on isDemo) */}
      <DemoWalkthroughCta />

      {/* Getting Started checklist — auto-hides once complete or dismissed.
          Held back until the vault + signed-proposal fetches settle so a
          fully-onboarded org never flashes an intermediate count before the
          card resolves to allDone and hides itself. */}
      {vaultsLoaded && signedProbeSettled && (
        <GettingStartedChecklist
          steps={gettingStartedSteps}
          storageKey={`ssp_getting_started_dismissed_${currentOrgId}`}
        />
      )}

      {/* Treasury command center — total value, signatures due, expiring soon */}
      {orgVaults.length > 0 && (
        <TreasuryHero vaults={orgVaults} signingRequests={signingRequests} />
      )}

      {/* Stats Grid */}
      <div className="dashboard-stats">
        <div className="stat-card" onClick={() => navigate('/organizations')}>
          <div className="stat-card-header">
            <span className="stat-card-label">{t('dashboard.organizations')}</span>
            <span className="stat-card-icon">
              <UsersIcon />
            </span>
          </div>
          <div className="stat-card-value">{organizations.length}</div>
          <div className="stat-card-subtitle">Total organizations</div>
        </div>

        <div className="stat-card" onClick={() => navigate('/members')}>
          <div className="stat-card-header">
            <span className="stat-card-label">{t('dashboard.teamMembers')}</span>
            <span className="stat-card-icon">
              <UserIcon />
            </span>
          </div>
          <div className="stat-card-value">{members.length || 0}</div>
          <div className="stat-card-subtitle">In current organization</div>
        </div>

        <div className="stat-card" onClick={() => navigate('/invitations')}>
          <div className="stat-card-header">
            <span className="stat-card-label">{t('dashboard.pendingInvites')}</span>
            <span className="stat-card-icon">
              <MailIcon />
            </span>
          </div>
          <div className="stat-card-value">{pendingOrgInvitations}</div>
          <div className="stat-card-subtitle">Awaiting response</div>
        </div>

        <div className="stat-card" onClick={() => navigate('/notifications')}>
          <div className="stat-card-header">
            <span className="stat-card-label">{t('dashboard.myInvitations')}</span>
            <span className="stat-card-icon">
              <ShieldCheckIcon />
            </span>
          </div>
          <div className="stat-card-value">{myInvitations.length}</div>
          <div className="stat-card-subtitle">Invitations to you</div>
        </div>
      </div>

      {/* Vault Summary */}
      {orgVaults.length > 0 && (
        <div className="dashboard-stats">
          <div className="stat-card" onClick={() => navigate('/vaults')}>
            <div className="stat-card-header">
              <span className="stat-card-label">Active Vaults</span>
              <span className="stat-card-icon">
                <BadgeCheckIcon />
              </span>
            </div>
            <div className="stat-card-value">{activeVaults}</div>
            <div className="stat-card-subtitle">Fully configured</div>
          </div>

          <div className="stat-card" onClick={() => navigate('/vaults')}>
            <div className="stat-card-header">
              <span className="stat-card-label">Pending Setup</span>
              <span className="stat-card-icon">
                <ClockIcon />
              </span>
            </div>
            <div className="stat-card-value">{pendingSetupVaults}</div>
            <div className="stat-card-subtitle">Awaiting xpubs</div>
          </div>

          <div className="stat-card" onClick={() => navigate('/vaults')}>
            <div className="stat-card-header">
              <span className="stat-card-label">Total Vaults</span>
              <span className="stat-card-icon">
                <FileLock2Icon />
              </span>
            </div>
            <div className="stat-card-value">{orgVaults.length}</div>
            <div className="stat-card-subtitle">All vaults</div>
          </div>
        </div>
      )}

      {/* Account Info */}
      <div className="dashboard-section">
        <div className="dashboard-section-header">
          <h2 className="dashboard-section-title">Your Account</h2>
          <button className="dashboard-section-link" onClick={() => navigate('/profile')}>
            Edit Profile <ChevronRightIcon />
          </button>
        </div>
        <div className="info-card">
          <div className="info-card-row">
            <span className="info-card-label">SSP Identity</span>
            <span className="info-card-value">
              <code>{user?.wkIdentity}</code>
            </span>
          </div>
          <div className="info-card-row">
            <span className="info-card-label">Enterprise Email</span>
            <span className="info-card-value">
              {user?.enterpriseEmail ? (
                user.enterpriseEmail
              ) : (
                <button className="dashboard-section-link" onClick={() => navigate('/profile')}>
                  Set up email <ChevronRightIcon />
                </button>
              )}
            </span>
          </div>
          <div className="info-card-row">
            <span className="info-card-label">Current Role</span>
            <span className="info-card-value">
              {currentRole && <Tag color={roleColors[currentRole]}>{roleLabels[currentRole]}</Tag>}
            </span>
          </div>
          {currentOrg?.membership.joinedAt && (
            <div className="info-card-row">
              <span className="info-card-label">
                <CalendarIcon /> Joined Organization
              </span>
              <span className="info-card-value">
                {new Date(currentOrg.membership.joinedAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Recent Team Members */}
      {members.length > 0 && (
        <div className="dashboard-section">
          <div className="dashboard-section-header">
            <h2 className="dashboard-section-title">Team Members</h2>
            <button className="dashboard-section-link" onClick={() => navigate('/members')}>
              View all ({members.length}) <ChevronRightIcon />
            </button>
          </div>
          <div className="info-card">
            {members.slice(0, 5).map((member) => {
              const memberId = member.wkIdentity || member.email || member.id;
              const email = member.email;
              const isCurrentUser = member.wkIdentity
                ? member.wkIdentity === user?.wkIdentity
                : member.email === user?.enterpriseEmail && !!member.email;
              const displayText = member.displayName
                ? member.displayName
                : member.wkIdentity
                  ? truncateAddress(member.wkIdentity)
                  : email || 'Unknown';
              const identityText =
                member.displayName && member.wkIdentity ? truncateAddress(member.wkIdentity) : null;

              return (
                <div key={memberId} className="info-card-row">
                  <span className="member-row">
                    {/* Shared identicon — same deterministic pattern as ssp-key /
                        ssp-wallet / proposal detail, so members are recognisable
                        across every SSP surface. */}
                    <Identicon value={memberId} size={28} />
                    <span className="member-info-col">
                      <span className="member-identity">{displayText}</span>
                      {identityText && <span className="member-email">{identityText}</span>}
                      {member.wkIdentity && email && <span className="member-email">{email}</span>}
                    </span>
                    {isCurrentUser && <Tag color="blue">You</Tag>}
                    {member.accountStatus === 'pending_wk' && <Tag color="warning">Pending</Tag>}
                  </span>
                  <span className="info-card-value">
                    <Tag color={roleColors[member.role]}>{roleLabels[member.role]}</Tag>
                  </span>
                </div>
              );
            })}
            {members.length > 5 && (
              <div className="dashboard-more-members">
                <button className="dashboard-section-link" onClick={() => navigate('/members')}>
                  +{members.length - 5} more members
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default DashboardPage;
